import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/catch';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/empty';
import 'rxjs/add/operator/retry';
import { ToastrService } from 'ngx-toastr';
import { afterGetCollectionCallbackInterface, afterGetObjectCallbackInterface, onErrorCallbackInterface } from '../interfaces/interfaces';
import { PagedResult } from '../models/paged-result.model';
import { PageConfiguration } from '../models/page-configuration.model';
import { Constants } from '../constants/constants';
import { Utils } from '../utils/utils';
import { ApiVersion, UrlService } from './url.service';
import { throwError } from 'rxjs';

export abstract class GenericBaseService {
  subject: string;
  apiVersion: ApiVersion;

  get httpHheaders(): HttpHeaders {
    return new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
  }

  constructor(
    subject: string,
    protected httpClient: HttpClient,
    protected toastrService: ToastrService,
    protected url: UrlService,
    apiVersion: ApiVersion = ApiVersion.v1
  ) {
    this.subject = subject;
    this.apiVersion = apiVersion;
  }

  /**
   * Gets the full API URL, resolving paths, and applies the predefined api version.
   * @param parts The additional path parts to append to the API base path.
   * @returns The resolved API URL.
   */
  getApiUrl(...parts: string[]): string {
    return this.url.getApiUrl(this.apiVersion, ...parts);
  }

  protected buildHttpHeaders(allowNoAuth = false): HttpHeaders {
    let headers = this.httpHheaders;

    if (allowNoAuth === true) {
      headers = headers.set('No-Auth', 'True');
    }

    return headers;
  }

  protected getFilterParams(filter: any, extraFilter?: any) {
    let params = new HttpParams();

    if (filter) {
      params = this.getParams(params, filter);
    }

    if (extraFilter) {
      params = this.getParams(params, extraFilter);
    }

    return params;
  }

  protected getParams(params: HttpParams, filter: any, customKey?: string) {
    Object.keys(filter).forEach(p => {
      if (filter[p] instanceof Array) {
        params = this.getParams(params, filter[p], p);
      } else {
        if (customKey) {
          params = params.append(customKey, this.getParamValue(filter[p]));
        } else {
          params = params.append(p.toString(), this.getParamValue(filter[p]));
        }
      }
    });

    return params;
  }

  protected getParamValue(value: any) {
    if ((value != undefined) && (value != null)) {
      if (value instanceof Date) {
        return value.toDateString();
      } else {
        return value.toString();
      }
    } else {
      return String();
    }
  }

  getCollection<T>(url: string, filter: any, afterGetCollectionCallback: afterGetCollectionCallbackInterface<T>) {
    this.get<T[]>(url, this.getFilterParams(filter))
      .subscribe(
        (data: T[]) => {
          if (data) {
            afterGetCollectionCallback(data);
          } else {
            afterGetCollectionCallback([]);
          }
        },
        error => {
          this.handleError(error);
        });
  }

  getCollectionPaginated<T>(url: string, pageConfiguration: PageConfiguration, filter: any, afterGetCollectionCallback: afterGetCollectionCallbackInterface<T>) {
    this.get<PagedResult<T>>(url, this.getFilterParams(pageConfiguration, filter))
      .subscribe(
        (data: PagedResult<T>) => {
          if (data) {
            this.updatePageConfiguration(pageConfiguration, data.PageNumber, data.PageSize, data.TotalNumberOfRecords);
            afterGetCollectionCallback(data.Results);
          } else {
            this.updatePageConfiguration(pageConfiguration, Constants.DefaultPageNumber, Constants.DefaultPageSize, Constants.DefaultTotalRecords);
            afterGetCollectionCallback([]);
          }
        },
        error => {
          this.handleError(error);
        });
  }

  protected updatePageConfiguration(pageConfiguration: PageConfiguration, pageNumber: number, pageSize: number, totalNumberOfRecords: number) {
    pageConfiguration.PageNumber = pageNumber;
    pageConfiguration.PageSize = pageSize;
    pageConfiguration.TotalRecords = totalNumberOfRecords;
  }

  getList<T>(url: string, filter: any = null, showErrorDetails: boolean = true, silentMode = false) {
    return this.get<Array<T>>(url, this.getFilterParams(filter), showErrorDetails, silentMode);
  }

  getListPaginated<T>(url: string, pageConfiguration: PageConfiguration, filter: any = null, showErrorDetails: boolean = true, silentMode = false) {
    return this.get<Array<T>>(url, this.getFilterParams(pageConfiguration, filter), showErrorDetails, silentMode);
  }

  public getObject<T>(url: string, filter: any, afterGetObjectCallback: afterGetObjectCallbackInterface<T>, showErrorDetails: boolean = true, silentMode = false, allowNoAuth = false) {
    this.get<T>(url, this.getFilterParams(filter), showErrorDetails, silentMode, allowNoAuth)
      .subscribe((data: T) => {
        afterGetObjectCallback(data);
      });
  }

  protected get<T>(url: string, params?: any, showErrorDetails: boolean = true, silentMode = false, allowNoAuth = false): Observable<T> {
    let httpHeaders: HttpHeaders = null;

    if (allowNoAuth === true) {
      httpHeaders = this.buildHttpHeaders(allowNoAuth);
    }

    const options = {
      headers: httpHeaders,
      params
    };

    return this.httpClient.get<T>(this.getApiUrl(url), options)
      .catch((error: HttpErrorResponse) => {
        return this.handleError(error, showErrorDetails, silentMode);
      });
  }

  protected getNow<T>(url: string, params?: any, afterGetCallback?: Function, customMessage?: string, showErrorDetails: boolean = true, silentMode = false, allowNoAuth = false): void {
    let httpHeaders: HttpHeaders = null;

    if (allowNoAuth === true) {
      httpHeaders = this.buildHttpHeaders(allowNoAuth);
    }

    const options = {
      headers: httpHeaders,
      params
    };

    this.httpClient.get<T>(this.getApiUrl(url), options)
      .catch((error: HttpErrorResponse) => {
        return this.handleError(error, showErrorDetails, silentMode);
      })
      .subscribe(
        (result: T) => {
          if (afterGetCallback) {
            afterGetCallback(result);
          }

          if (customMessage) {
            this.toastrService.success(customMessage);
          }
        });
  }

  protected post<T>(url: string, body, showErrorDetails: boolean = true, silentMode = false): Observable<object> {
    return this.httpClient.post<T>(this.getApiUrl(url), body, {headers: this.httpHheaders})
      .catch((error: HttpErrorResponse) => {
        return this.handleError(error, showErrorDetails, silentMode);
      });
  }

  protected postNow<T>(url: string, body, afterPostCallback?: Function, customMessage?: string, showErrorDetails: boolean = true, silentMode = false, allowNoAuth = false, onErrorCallback?: onErrorCallbackInterface) {
    const httpHeaders = this.buildHttpHeaders(allowNoAuth);

    this.httpClient.post<T>(this.getApiUrl(url), body, {headers: httpHeaders})
      .catch((error: HttpErrorResponse) => {
        return this.handleError(error, showErrorDetails, silentMode);
      })
      .subscribe(
        (result: T) => {
          if (afterPostCallback) {
            afterPostCallback(result);
          }

          if (customMessage) {
            this.toastrService.success(customMessage);
          } else {
            this.toastrService.success(this.subject + ' ' + 'added successfully');
          }
        },
        // handleError has already reported the failure; this lets a caller additionally react to it - to
        // point at the field that caused it, say. It also gives the rethrown error a subscriber, where
        // before it surfaced as an unhandled rejection.
        (error: HttpErrorResponse) => {
          if (onErrorCallback) {
            onErrorCallback(error);
          }
        });
  }

  protected put<T>(url: string, body, showErrorDetails: boolean = true, silentMode = false): Observable<object> {
    return this.httpClient.put<T>(this.getApiUrl(url), body, {headers: this.httpHheaders})
      .catch((error: HttpErrorResponse) => {
        return this.handleError(error, showErrorDetails, silentMode);
      });
  }

  protected putNow<T>(url: string, body, afterPutCallback?: Function, customMessage?: string, showErrorDetails: boolean = true, silentMode = false, onErrorCallback?: onErrorCallbackInterface) {
    this.httpClient.put<T>(this.getApiUrl(url), body, {headers: this.httpHheaders})
      .catch((error: HttpErrorResponse) => {
        return this.handleError(error, showErrorDetails, silentMode);
      })
      .subscribe(
        (result: T) => {
          if (afterPutCallback) {
            afterPutCallback(result);
          }

          if (customMessage) {
            this.toastrService.success(customMessage);
          } else {
            this.toastrService.success(this.subject + ' ' + 'updated successfully');
          }
        },
        // See postNow: reporting is already done, this is for a caller that wants to react as well.
        (error: HttpErrorResponse) => {
          if (onErrorCallback) {
            onErrorCallback(error);
          }
        });
  }

  //protected delete<T>(url: string, params): any {
  protected delete<T>(url: string): any {
    return this.httpClient.delete<T>(this.getApiUrl(url))
      .catch((error: HttpErrorResponse) => {
        return this.handleError(error);
      });
  }

  protected deleteNow(url: string, afterDeleteCallback?: Function, customMessage?: string) {
    this.httpClient.delete(this.getApiUrl(url))
      .catch((error: HttpErrorResponse) => {
        return this.handleError(error);
      })
      .subscribe(
        () => {
          if (afterDeleteCallback) {
            afterDeleteCallback();
          }

          if (customMessage) {
            this.toastrService.info(customMessage);
          } else {
            this.toastrService.info(this.subject + ' ' + 'is deleted');
          }
        });
  }

  public activateNow(url: string, afterPutCallback?: Function, customMessage?: string) {
    this.httpClient.put(this.getApiUrl(url), {headers: this.httpHheaders})
      .catch((error: HttpErrorResponse) => {
        return this.handleError(error);
      })
      .subscribe(
        () => {
          if (afterPutCallback) {
            afterPutCallback();
          }

          if (customMessage) {
            this.toastrService.success(customMessage);
          } else {
            this.toastrService.success(this.subject + ' ' + 'activated successfully');
          }
        });
  }

  public deactivateNow(url: string, afterPutCallback?: Function, customMessage?: string) {
    this.httpClient.put(this.getApiUrl(url), {headers: this.httpHheaders})
      .catch((error: HttpErrorResponse) => {
        return this.handleError(error);
      })
      .subscribe(
        () => {
          if (afterPutCallback) {
            afterPutCallback();
          }

          if (customMessage) {
            this.toastrService.info(customMessage);
          } else {
            this.toastrService.info(this.subject + ' ' + 'is deactivated');
          }
        });
  }

  protected getBody(object: any, ignoreList?: any[]) {
    return JSON.stringify(object, function (key, value) {
      if ((ignoreList) && (ignoreList.indexOf(key) > -1)) {
        return undefined;
      } else {
        return value;
      }
    });
  }

  private handleError(error: HttpErrorResponse, showErrorDetails: boolean = true, silentMode = false): Observable<any> {
    if (silentMode) {
      Utils.reportErrorToConsole(error);
    } else if (showErrorDetails) {
      Utils.handleSimpleError(this.toastrService, error);
    } else {
      this.toastrService.error('An unknown error has occurred.');
    }

    return throwError(error);
  }
}
